import { useState, useEffect, useCallback } from 'react';

interface LoadingBarProps {
  onComplete: () => void;
  duration?: number;
  label?: string;
}

const SEGMENTS = 20;

// Discrete pixel-art color palette: red → orange → yellow → green → cyan → blue
const SEGMENT_COLORS = [
  '#FF004D', '#FF1A3C', '#FF3C2B', '#FF5E1A',
  '#FF8000', '#FFA300', '#FFC600', '#FFEC27',
  '#D4F028', '#A8F429', '#7CF82A', '#50FC2B',
  '#24FF2C', '#00F850', '#00E890', '#00D8C0',
  '#00C8F0', '#0098FF', '#0068FF', '#2948FF',
];

const LoadingBar = ({ onComplete, duration = 2000, label = 'LOADING' }: LoadingBarProps) => {
  const [progress, setProgress] = useState(0);

  const stableOnComplete = useCallback(onComplete, []);

  useEffect(() => {
    const start = performance.now();
    let raf: number;

    const tick = (now: number) => {
      const elapsed = now - start;
      const pct = Math.min(elapsed / duration, 1);
      setProgress(pct);
      if (pct < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        stableOnComplete();
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [stableOnComplete]);

  const filledSegments = Math.floor(progress * SEGMENTS);

  return (
    <div className="absolute inset-0 z-50 bg-black flex flex-col items-center justify-center gap-8">
      <p
        className="font-mono-game text-3xl tracking-[0.3em] text-white"
        style={{ textShadow: '0 0 8px rgba(255,255,255,0.5)' }}
      >
        {label}
      </p>

      {/* Bar container — chunky pixel border */}
      <div
        className="relative"
        style={{
          width: 420,
          height: 48,
          border: '6px solid #fff',
          imageRendering: 'pixelated',
        }}
      >
        {/* Segments */}
        <div className="flex h-full gap-[3px] p-[4px]">
          {Array.from({ length: SEGMENTS }).map((_, i) => {
            const filled = i < filledSegments;
            return (
              <div
                key={i}
                className="flex-1 h-full"
                style={{
                  backgroundColor: filled
                    ? SEGMENT_COLORS[i]
                    : 'rgba(255,255,255,0.08)',
                  transition: 'background-color 0.05s',
                  imageRendering: 'pixelated',
                }}
              />
            );
          })}
        </div>
      </div>

      <p className="font-mono-game text-sm text-white/60 tracking-widest">
        {Math.floor(progress * 100)}%
      </p>
    </div>
  );
};

export default LoadingBar;
